/**
 * useSubmitFeedback hook
 * React Query mutation exposing the one slot ParentFeedbackSheet needs to drive
 * its UI: submit, isPending, error, isSuccess, reset.
 *
 * Implements US-008 (T2). Near-verbatim clone of the resetPassword mutation slot
 * in useChildMutations.ts:129-157, and returns the same five-member shape as
 * ChildMutationSlot (useChildMutations.ts:55-66) — the exact contract three
 * shipped dialogs already drive their UI from.
 *
 * TO-LL-036 enforcement: NO onError handler and NO try/catch here. React Query
 * holds the error on `error`; the sheet reads it and renders a FIXED
 * client-owned string, never the error's own text (see ParentFeedbackSheet's
 * errorCopyFor). No retry option is added: React Query's mutation default is
 * already zero retries, and adding one would produce duplicate feedback
 * records — IC-001 explicitly records there is no idempotency key and no
 * transactional dedupe guard behind this endpoint (BR-008.1).
 *
 * Deliberately NO onSuccess invalidateQueries. The three child mutations in
 * useChildMutations invalidate ['parent','dashboard'] because they change what
 * the dashboard displays. Submitting feedback changes nothing the parent app
 * renders, so invalidating here would be a pointless refetch of the whole
 * dashboard on every send. Do not add it back.
 */

import { useMutation } from '@tanstack/react-query';
import { submitFeedback } from '@/services/feedback';
import type { FeedbackPayload, FeedbackSubmitResponse } from '@/types/feedback';
import type { AuthError } from '@/types/auth';

/**
 * Shape of the mutation slot returned by useSubmitFeedback.
 * Mirrors ChildMutationSlot (useChildMutations.ts:55-66); `mutate` is renamed
 * to `submit` so a reader of the sheet's call site sees what is being sent.
 */
export interface UseSubmitFeedbackReturn {
  /**
   * Trigger the submission. Accepts an optional second argument carrying
   * per-call React Query mutate options (onSuccess / onError), which is the
   * SUPPORTED way for a caller to react once to a single settled mutation
   * (BUG-20260803-192629, RC-001, fixed 2026-08-04). This is a TYPE-ONLY
   * widening: `submit` is `mutation.mutate` below, and mutation.mutate
   * already carries this (variables, options?) overload at runtime.
   */
  submit: (
    payload: FeedbackPayload,
    options?: { onSuccess?: () => void; onError?: () => void }
  ) => void;
  /** True while the request is in-flight — the sole source of truth for the disabled submit button (BR-008.1). */
  isPending: boolean;
  /** Error from the axios interceptor: {code, message}; null when no error. Never rendered directly by the sheet. */
  error: AuthError | null;
  /**
   * HAZARD WARNING (BUG-20260803-192629, RC-001): this flag is retained ONLY
   * for parity with ChildMutationSlot (useChildMutations.ts:55-66), whose
   * five-member shape this hook deliberately mirrors. It has NO release path
   * in a consumer that does not unmount: ParentFeedbackSheet does not
   * unmount when closed (its early return only suppresses DOM output), so
   * once this flips true it STAYS true for the rest of the page's life,
   * until reset() runs on the sheet's next open. NEVER derive a side effect
   * from this flag — doing exactly that (an isSuccess-keyed useEffect) was
   * the root cause of BUG-20260803-192629: the toast/close pair re-fired on
   * every render whose callback identities changed, because the flag never
   * released. React once per call instead, via
   * submit(payload, { onSuccess }). Note that ChildMutationSlot's own
   * isSuccess JSDoc ("triggers dialog close via useEffect") describes
   * exactly the pattern this warning exists to rule out; do not follow it
   * here.
   */
  isSuccess: boolean;
  /** Reset mutation state — called when the sheet reopens to clear a stale error. */
  reset: () => void;
}

/**
 * React Query mutation for submitting parent feedback.
 *
 * @returns { submit, isPending, error, isSuccess, reset }
 */
export function useSubmitFeedback(): UseSubmitFeedbackReturn {
  const mutation = useMutation<FeedbackSubmitResponse, AuthError, FeedbackPayload>({
    mutationFn: (payload) => submitFeedback(payload),
  });

  return {
    submit: mutation.mutate,
    isPending: mutation.isPending,
    error: mutation.error,
    isSuccess: mutation.isSuccess,
    reset: mutation.reset,
  };
}
